import { useCallback, useEffect, useRef, useState } from "react"
import ScrollUpArrow from "../../Components/Components/ScrollUpArrow"
import ErrorBoundary from "../../Components/ErrorBoundary/ErrorBoundary"
import NoAccess from "../../Components/NoAccess"
import { useAuth } from "../../Providers/AuthProvider"
import { useCrm } from "../../Providers/CrmProvider"
import LoadingSpinner from "../../Components/Components/LoadingSpinner"
import { useParams } from "../../Providers/ParamsProvider"
import CrmEvents from "./Components/CrmEvents"
import { toast } from "react-toastify"
import CrmEventsFilter from "./Components/CrmEventsFilter"
import AddEditCrmTaskModal from "../../Components/Components/Modals/AddEditCrmTaskModal"
import MobileAccountNavBar from "../../Components/MobileComponents/NavBarMobile.jsx/MobileAccountNavBar"
import { AccountCurrentView } from "../../Data/PageModeEnums.ts"

const CrmEventsPageIndex = () => {
    const observer = useRef()
    const { authState } = useAuth()
    const { handleGetCrmEvents } = useCrm()
    const { isMobile } = useParams()

    const [pageNumber, setPageNumber] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [pageSize, setPageSize] = useState(12)
    const [crmEvents, setCrmEvents] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [filterCriteria, setFilterCriteria] = useState({ type: null, dateFrom: null, dateTo: null })
    const [showAddCrmTaskModal, setShowAddCrmTaskModal] = useState(false)

    const handleRefreshData = () => {
        setPageNumber(1)
        setCrmEvents([])
        setHasMore(true)
        fetchData(true)
    }

    useEffect(() => {
        handleRefreshData()
    }, [authState.custId, filterCriteria])

    useEffect(() => {
        if (pageNumber > 1) {
            fetchData(false)
        }
    }, [pageNumber])

    const fetchData = async (isInitial = false) => {
        setIsLoading(true)
        const result = await handleGetCrmEvents(
            isInitial ? 1 : pageNumber,
            pageSize,
            filterCriteria.type,
            filterCriteria.dateFrom,
            filterCriteria.dateTo
        )
        if (result && result.events) {
            const newEvents = result.events
            setCrmEvents(prev => isInitial ? newEvents : [...prev, ...newEvents])
            setHasMore(newEvents.length === pageSize)
        } else {
            if (isInitial) setCrmEvents([])
            setHasMore(false)
        }
        setIsLoading(false)
    }

    const lastBookElementRef = useCallback(node => {
        if (isLoading) {
            return
        }
        if (observer.current) observer.current.disconnect()
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPageNumber(prevPageNumber => prevPageNumber + 1)
            }
        })
        if (node) observer.current.observe(node)
    }, [isLoading, hasMore])

    const handleAddCrmEventButtonClick = () => {
        if (Number(authState.user?.upR_SYS_OBSLUGA_KONTAKTOW) < 2) {
            toast.warning('Brak uprawnień')
            return
        }
        setShowAddCrmTaskModal(true)
    }

    const handleAddCrmTaskModalDisappear = (answer) => {
        if (answer) {
            handleRefreshData()
        }
        setShowAddCrmTaskModal(false)
    }

    if (!authState.user?.isOperator || Number(authState.user?.upR_SYS_OBSLUGA_KONTAKTOW) < 1) {
        return <NoAccess />
    }
    if (!authState.custId) {
        return <NoAccess customErrorText="Brak wybranego kontrahenta!" />
    }
    return (<>
        <ErrorBoundary>
            <AddEditCrmTaskModal
                show={showAddCrmTaskModal}
                onHide={handleAddCrmTaskModalDisappear}
                type={'Dowolne zdarzenie CRM'}
            />
            <ScrollUpArrow />
            <div className="container-fluid-with-paddings mt-4 mb-4" style={{ marginBottom: isMobile ? '80px' : '0px' }}>
                {isMobile && <div className="mb-2"><MobileAccountNavBar currentKey={AccountCurrentView.ZdarzeniaCRM} /></div>}
                <div className="main-container-holder">
                    <div className="row mb-2">
                        <div className="col-12 col-xl-3">
                            <h3>Zdarzenia CRM</h3>
                        </div>
                        <div className="col-12 col-xl-7">
                            <CrmEventsFilter
                                filterCriteria={filterCriteria}
                                setFilterCriteria={setFilterCriteria}
                            />
                        </div>
                        <div className="col-12 col-xl-2 d-flex justify-content-start justify-content-xl-end h-50">
                            <button className="btn-app-secondary" onClick={handleAddCrmEventButtonClick}>Dodaj zdarzenie</button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <div className='table-responsive'>
                                <table className={`${isMobile ? 'table-font-small' : ''} table table-bordered`}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: "3%" }}>LP</th>
                                            <th style={{ width: "12%" }}>ZDARZENIE</th>
                                            <th style={{ width: "23%" }}>TEMAT</th>
                                            <th style={{ width: "10%" }}>DATA</th>
                                            <th style={{ width: "10%" }}>OCENA</th>
                                            <th style={{ width: "50%" }}>UWAGI</th>
                                            <th style={{ width: "2%" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <CrmEvents
                                            crmEvents={crmEvents}
                                            setCrmEvents={setCrmEvents}
                                            lastBookElementRef={lastBookElementRef}
                                            isLoading={isLoading}
                                        />
                                    </tbody>
                                </table>
                                {isLoading && <LoadingSpinner />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    </>)
}

export default CrmEventsPageIndex